import React, { useEffect, useRef, useState } from "react";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core";
import { PostmortemServices } from "../../services/postmortemServices";
import RecurrenceMatchFeedback, { MatchTitle, getLatestVerdict } from "./RecurrenceMatchFeedback";

const LABELS = {
  RECURRING: "Likely recurring",
  RELATED: "Related incident",
  NOT_RECURRING: "Not recurring",
  INSUFFICIENT_EVIDENCE: "Insufficient evidence",
};

// High cutoff matches the agent's 0.7 single-run trust threshold (deep_agent_recurrence/agent.py).
export function getConfidenceLevel(score, disagreed = false) {
  if (score == null || Number.isNaN(Number(score))) return null;
  const value = Number(score);
  if (value >= 0.7 && !disagreed) return { key: "high", label: "High", color: "#2c8a4d" };
  if (value >= 0.5) return { key: "moderate", label: "Moderate", color: "#b86b00" };
  return { key: "low", label: "Low", color: "#b80012" };
}

function ConfidenceBadge({ score, disagreed = false }) {
  const level = getConfidenceLevel(score, disagreed);
  if (!level) return null;
  return <span style={{ color: level.color, fontWeight: 600 }}>{level.label}</span>;
}

function RecurrenceAnalysisPanel({ incidentId, canAnalyze, onRecurrenceChanged }) {
  const {
    getRecurrenceAnalysisStatus,
    triggerRecurrenceAnalysis,
    recordRecurrenceFeedback,
  } = PostmortemServices();
  const [analysis, setAnalysis] = useState({ status: "NOT_STARTED" });
  const [error, setError] = useState("");
  // Verdict requests run one at a time: the server rewrites the whole feedback list on each call.
  const feedbackQueue = useRef(Promise.resolve());

  const loadStatus = async () => {
    try {
      setAnalysis(await getRecurrenceAnalysisStatus(incidentId));
      setError("");
    } catch (requestError) {
      setError("Unable to load recurrence analysis.");
    }
  };

  useEffect(() => {
    if (incidentId) loadStatus();
  }, [incidentId]);

  useEffect(() => {
    if (analysis.status !== "PENDING" && analysis.status !== "QUEUED") return undefined;
    const timer = window.setInterval(loadStatus, 8000);
    return () => window.clearInterval(timer);
  }, [analysis.status, incidentId]);

  const handleAnalyze = async () => {
    if (!incidentId) {
      setError("Cannot start recurrence analysis: incident ID is missing from this report link.");
      return;
    }
    try {
      setError("");
      await triggerRecurrenceAnalysis(incidentId);
      setAnalysis((current) => ({ ...current, status: "PENDING" }));
    } catch (requestError) {
      setError("Unable to start recurrence analysis.");
    }
  };

  const submitFeedback = (match, verdict, reason = "", note = "") => {
    const entry = {
      matchIncidentId: match.incidentId,
      matchPostMortemId: match.postMortemId,
      verdict,
      reason,
      note,
      source: "ANALYSIS_TAB",
    };
    setError("");
    // Show the verdict right away; the server reply only confirms it.
    setAnalysis((current) => ({ ...current, feedback: [...(current.feedback || []), entry] }));

    feedbackQueue.current = feedbackQueue.current.then(async () => {
      try {
        const updated = await recordRecurrenceFeedback(incidentId, entry);
        setAnalysis(updated);
        if (onRecurrenceChanged) onRecurrenceChanged();
      } catch (requestError) {
        setAnalysis((current) => ({
          ...current,
          feedback: (current.feedback || []).filter((item) => item !== entry),
        }));
        setError("Unable to save your feedback on this match.");
      }
    });
  };

  const result = analysis.result;
  const consensus = result?.consensus;
  const disagreed = consensus?.unanimous === false;
  const confidenceLevel = getConfidenceLevel(result?.confidence, disagreed);
  const needsManualReview = !!confidenceLevel && confidenceLevel.key !== "high";
  const voteSummary = consensus?.voteCounts
    ? Object.entries(consensus.voteCounts)
        .map(([rec, count]) => `${LABELS[rec] || rec} x${count}`)
        .join(", ")
    : null;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "center" }}>
        <div>
          <strong style={{ display: "block", fontSize: "14px" }}>AI Recurrence Analysis</strong>
          <span style={{ color: "#545454", fontSize: "13px" }}>
            Compares this failure mode with historical postmortems and transcripts.
          </span>
        </div>
        {canAnalyze && (
          <McButton
            appearance="primary"
            variant="outlined"
            fit="small"
            icon="sparkle"
            label={result ? "Re-analyze" : "Analyze"}
            disabled={!incidentId || analysis.status === "PENDING" || analysis.status === "QUEUED"}
            onClick={handleAnalyze}
          />
        )}
      </div>
      {canAnalyze && !incidentId && (
        <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#545454" }}>
          Recurrence analysis is unavailable because this postmortem has no linked incident ID.
        </p>
      )}

      {(analysis.status === "PENDING" || analysis.status === "QUEUED") && (
        <p style={{ margin: "14px 0 0", fontSize: "13px" }}>
          {analysis.status === "QUEUED" ? "Analysis queued..." : "Analysis in progress..."}
        </p>
      )}
      {error && <p style={{ color: "#b80012", margin: "14px 0 0" }}>{error}</p>}

      {result && (
        <div style={{ marginTop: "16px" }}>
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "center" }}>
            <span><strong>Recommendation:</strong> {LABELS[result.recommendation] || result.recommendation}</span>
            {confidenceLevel && (
              <span>
                <strong>Confidence:</strong> <ConfidenceBadge score={result.confidence} disagreed={disagreed} />
              </span>
            )}
          </div>
          {consensus && (
            <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#545454" }}>
              Based on {consensus.runs} analysis {consensus.runs === 1 ? "run" : "runs"}
              {consensus.runs > 1 ? ` (${consensus.unanimous ? "unanimous" : "disagreed"}${voteSummary ? `: ${voteSummary}` : ""})` : ""}
            </p>
          )}
          {needsManualReview && (
            <div
              style={{
                marginTop: "8px",
                padding: "8px 12px",
                background: "#fff4e5",
                border: "1px solid #f0b429",
                borderRadius: "4px",
                fontSize: "13px",
                color: "#6b4e00",
              }}
            >
              <strong>{confidenceLevel.label} confidence{disagreed ? " — AI runs disagreed" : ""}:</strong>{" "}
              {consensus?.runs > 1
                ? `Ran ${consensus.runs} independent analyses${voteSummary ? ` (${voteSummary})` : ""}; treat this result as uncertain and verify manually.`
                : "This result is below the confidence threshold; verify manually before accepting."}
            </div>
          )}
          {result.summary && (
            <div style={{ margin: "10px 0" }}>
              {result.summary
                .split(/(?<=[.!?])\s+(?=[A-Z])/)
                .filter(Boolean)
                .map((sentence, idx) => (
                  <p key={idx} style={{ margin: "0 0 8px" }}>{sentence}</p>
                ))}
            </div>
          )}
          {result.counterArgument && (
            <details style={{ marginBottom: "12px" }}>
              <summary style={{ fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                AI self-critique (counter-argument)
              </summary>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#545454" }}>{result.counterArgument}</p>
            </details>
          )}
          {Array.isArray(result.investigationLog) && result.investigationLog.length > 0 && (
            <details style={{ marginBottom: "12px" }}>
              <summary style={{ fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                Investigation steps ({result.investigationLog.length})
              </summary>
              <ul style={{ margin: "6px 0 0", paddingLeft: "20px", fontSize: "13px", color: "#545454" }}>
                {result.investigationLog.map((step, idx) => (
                  <li key={idx} style={{ marginBottom: "4px" }}>{step}</li>
                ))}
              </ul>
            </details>
          )}
          {Array.isArray(result.matches) && result.matches.length > 0 && (
            <div>
              <strong style={{ fontSize: "13px" }}>Matched incidents</strong>
              {result.matches.map((match) => (
                <div key={`${match.incidentId}-${match.postMortemId}`} style={{ padding: "10px 0", borderBottom: "1px solid #e2e2e2" }}>
                  <div style={{ fontWeight: 600 }}>
                    <MatchTitle match={match} />
                    {match.confidence != null && (
                      <> (<ConfidenceBadge score={match.confidence} />)</>
                    )}
                  </div>
                  {Array.isArray(match.matchingEvidence) && match.matchingEvidence.length > 0 && (
                    <div style={{ fontSize: "13px", marginTop: "4px" }}>
                      {match.matchingEvidence.join("; ")}
                    </div>
                  )}
                  {Array.isArray(match.differences) && match.differences.length > 0 && (
                    <div style={{ color: "#545454", fontSize: "13px", marginTop: "4px" }}>
                      Differences: {match.differences.join("; ")}
                    </div>
                  )}
                  {canAnalyze && (
                    <div style={{ marginTop: "8px" }}>
                      <RecurrenceMatchFeedback
                        match={match}
                        verdict={getLatestVerdict(analysis.feedback, match.incidentId)}
                        onAccept={(selected) => submitFeedback(selected, "ACCEPTED")}
                        onReject={(selected, reason, note) => submitFeedback(selected, "REJECTED", reason, note)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RecurrenceAnalysisPanel;